import { Injectable } from '@angular/core';
import {Store} from "@ngrx/store";
import * as fromStore from "../../../store";
import {DbStoreState} from "../../../store";
import { Router } from '@angular/router';
import { GoogleAnalyticsService } from '../../../shared/services/google-analytics/google-analytics.service';
import { first } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { OneWeekFreeDemoUpgradeComponent } from '../../../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component';
import {DbModalService} from "../../../modules/modal/modal";
import {UserService} from "../../../user/user.service";
import {clientRoles} from "../../../user/user-roles-actions";
import {TypeUtils} from "../../../shared/utils/type-utils";
import {RequesterService} from "../requester/requester.service";
import {
  PricingPageViewComponent
} from "../../../shared/components/dialogs/one-month-free/components/pricing-page-view/pricing-page-view.component";

@Injectable({
  providedIn: 'root'
})
export class SidebarNavigationService {
  protected readonly clientRoles = clientRoles;
  isWorkspacePopupOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isSidebarVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isProductHuntBannerVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private ownerWorkspaceId: any;
  private currentWorkspaceId: any;

  constructor(
    private router: Router,
    private store: Store<fromStore.DbStoreState>,
    private userService: UserService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private modalService: DbModalService,
    private requester: RequesterService,
    private _store: Store<DbStoreState>,
  ) {
    this._getWorkspaceEntities();
  }

  private _getWorkspaceEntities(): void {
    this._store.select(fromStore.getWorkspaceIdEntities)
      .subscribe(workspaceIdEntities => {
        this.ownerWorkspaceId = workspaceIdEntities.ownerWorkspaceId;
        this.currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || this.ownerWorkspaceId;
      });
  }

  checkPaymentsAvailability() {
    return this.requester.tenWebStripe
      .get(`workspaces/${this.currentWorkspaceId}/accounts`, { force: 1 }, true);
  }

  /**
   * Toggle the primary sidebar visibility
   * @param force Optionally force a specific state (true to show, false to hide)
   */
  public toggleSidebar(force?: boolean): void {
    if (typeof force === 'boolean') {
      this.isSidebarVisible.next(force);
    } else {
      this.isSidebarVisible.next(!this.isSidebarVisible.value);
    }

    // Always close workspace popup when toggling sidebar
    this.isWorkspacePopupOpen.next(false);
  }

  public switchToUserManagement(userLocalStorage: any, switchLoading: (loading: boolean) => void): void {
    if (!userLocalStorage?.switchToUserManagement) {
      switchLoading(true);
      const updatedLocalStorage = {...userLocalStorage, switchToUserManagement: true};
      this.userService.setToLocal(updatedLocalStorage)
        .pipe(first())
        .subscribe(response => {
          if (response?.success) {
            this.store.dispatch(new fromStore.LoadUser());
            this.store.dispatch(new fromStore.LoadUserLocalStorage());
            setTimeout(() => {
              switchLoading(false);
              this.router.navigateByUrl('/websites');
            }, 3000);
          } else {
            switchLoading(false);
          }
        });
    } else {
      this.router.navigateByUrl('/websites');
    }

    this.sendAnalyticsEvent('Dashboard: Website Management');
  }

  public switchToDomains(userLocalStorage: any, userSubscription: any, currentWorkspace: any, switchLoading: (loading: boolean) => void): void {
    this.sendAnalyticsEvent('Dashboard: Domains Management');

    const canAccessPortfolio = userSubscription?.hasActiveDomainAgreement ||
      ![this.clientRoles.OWNER, this.clientRoles.COMPANY_ADMIN].includes(currentWorkspace?.roleId);

    if (!userLocalStorage.switchToDomainsMenu) {
      switchLoading(true);
      const updatedLocalStorage = {...userLocalStorage, switchToDomainsMenu: true};
      this.userService.setToLocal(updatedLocalStorage)
        .pipe(first())
        .subscribe(response => {
          if (response.success) {
            this.store.dispatch(new fromStore.LoadUser());
            this.store.dispatch(new fromStore.LoadUserLocalStorage());
            setTimeout(() => {
              switchLoading(false);
              this.router.navigateByUrl(canAccessPortfolio ? 'domains-handler/portfolio' : 'domains-handler/search');
            }, 3000);
          } else {
            switchLoading(false);
          }
        });
    } else {
      this.router.navigateByUrl(canAccessPortfolio ? 'domains-handler/portfolio' : 'domains-handler/search');
    }
  }

  public switchToPayments(userLocalStorage: any, switchLoading: (loading: boolean) => void): void {
    if (!userLocalStorage.switchToPayments) {
      switchLoading(true);
      const updatedLocalStorage = {...userLocalStorage, switchToPayments: true};
      this.userService.setToLocal(updatedLocalStorage)
        .pipe(first())
        .subscribe(response => {
          if (response.success) {
            this.store.dispatch(new fromStore.LoadUser());
            this.store.dispatch(new fromStore.LoadUserLocalStorage());
            setTimeout(() => {
              switchLoading(false);
              this.router.navigateByUrl('/payments-management');
            }, 3000);
          } else {
            switchLoading(false);
          }
        });
    } else {
      this.router.navigateByUrl('/payments-management');
    }

    this.sendAnalyticsEvent('Dashboard: 10Web Payments');
  }

  showUpgradeDialog(label: string, isDemoUser: boolean): void {
    const analyticsData = {
      eventCategory: 'Free Upgrade Offer',
      eventAction: "Locked button click",
      eventLabel: label
    };
    if (isDemoUser) {
      const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
      const options = {
        overflowHidden: false,
        backdropClass: 'blurred',
      };
      if (!firstTime) {
        options['width'] = 1240;
      } else {
        options['width'] = 460;
        options['height'] = 600;
      }
      const modal = this.modalService.open(OneWeekFreeDemoUpgradeComponent, options);
      modal.componentInstance.showFirstTime = firstTime;
    } else {
      this.modalService.open(PricingPageViewComponent, { overflowHidden: true, height: 960, width: 1076});
    }
    this.googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  private sendAnalyticsEvent(label: string): void {
    const analyticsData = {
      eventCategory: 'Free Upgrade Offer',
      eventAction: "Locked button click",
      eventLabel: label
    };
    this.googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }
}
